import { defineStore } from "pinia";
import { computed, ref } from "vue";

interface RawClaim {
  type: string;
  value: string;
  valueType: string | null;
}

interface UserClaims {
  sessionId: string;
  email: string;
  givenName: string;
  surname: string;
  name: string;
  organisations: string[];
  role: string;
  logoutUrl: string;
  sessionExpiresIn: number;
  sessionState: string;
}

export const useUserStore = defineStore('user-store', () => {
  const user = ref<UserClaims | null>(null);
  const authorityUrl = ref(import.meta.env.VITE_APP_AUTHORITY_URL);

  const parse = (claims: RawClaim[]): UserClaims => {
    const claimsMap = new Map<string, string>();
    const organisations: string[] = [];
    
    // Build a map of claims and collect organisations
    claims.forEach(claim => {
      if (claim.type === 'organisation') {
        organisations.push(claim.value);
      } else {
        claimsMap.set(claim.type, claim.value);
      }
    });
    
    return {
      sessionId: claimsMap.get('sid') ?? '',
      email: claimsMap.get('preferred_username') ?? '',
      givenName: claimsMap.get('given_name') ?? '',
      surname: claimsMap.get('family_name') ?? '',
      name: claimsMap.get('name') ?? '',
      organisations,
      role: claimsMap.get('role') ?? '',
      logoutUrl: claimsMap.get('bff:logout_url') ?? '',
      sessionExpiresIn: parseInt(claimsMap.get('bff:session_expires_in') ?? '0'),
      sessionState: claimsMap.get('bff:session_state') ?? '',
    };
  };
  
  const fetchUser = async () => {
    if (user.value !== null) {
      return;
    }

    const res = await fetch('/bff/user', {
      credentials: 'include',
      headers: {
        'X-CSRF': '1',
      },
    });

    if (res.ok) {
      user.value = parse(await res.json());
    } else {
      user.value = null;
    }
  };

  const signin = async () => {
    window.location.href = '/bff/login';
  };

  const signout = () => {
    if (!user.value) {
      return;
    }

    window.location.href = user.value.logoutUrl;
  };

  const organisations = computed<string[]>(() => {
    const orgs: string[] = user.value?.organisations ?? [];

    return orgs.sort((a, z) => a.localeCompare(z));
  });
  const isSystem = computed<boolean>(() => user.value?.role === 'System');
  const role = computed<string | null>(() => user.value?.role ?? null);

  return {
    fetchUser,
    signin,
    signout,
    user,
    isSystem,
    organisations,
    role,
    authorityUrl,
  };
});